// AbNotificationBell — Benachrichtigungs-Glocke fuer KI-Regelset-Vorschlag-Jobs (Vorbild
// OC-Pilot ai/AiSuggestContext.tsx NotificationBell), hier VEREINFACHT: kein Server-Ack-Endpoint
// im Vertrag (api.js kennt nur listSuggestJobs/getSuggestJob), daher Quittierung per localStorage
// (Bauplan: "Badge = done unbestaetigt [localStorage-Ack wie OC-Pilot vereinfacht]"). Der Job-DTO
// traegt keinen Dateinamen (nur documentId) — wir zeigen daher "Dokument Nr. X" statt eines Titels,
// um keine erfundenen Daten vorzutaeuschen.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Bell, Loader2, CheckCircle2, AlertTriangle, X } from 'lucide-react';
import { HeaderIconButton } from '../ui';
import { listSuggestJobs } from '../../api';

const POLL_MS = 12000;
const ACK_STORAGE_KEY = 'ab.suggestAck.v1';

const loadAcked = () => {
  try { return new Set(JSON.parse(localStorage.getItem(ACK_STORAGE_KEY) || '[]')); }
  catch { return new Set(); }
};
const saveAcked = (set) => {
  try { localStorage.setItem(ACK_STORAGE_KEY, JSON.stringify(Array.from(set))); } catch { /* Storage voll/gesperrt -> egal */ }
};

const AbNotificationBell = ({ user, isDarkMode, t, onOpenDocument }) => {
  const [jobs, setJobs] = useState([]);
  const [acked, setAcked] = useState(loadAcked);
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  const refresh = useCallback(() => {
    listSuggestJobs(user).then((res) => setJobs(Array.isArray(res) ? res : [])).catch(() => {});
  }, [user]);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, POLL_MS);
    return () => clearInterval(id);
  }, [refresh]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const running = jobs.filter((j) => j.status === 'running');
  const unread = jobs.filter((j) => (j.status === 'done' || j.status === 'error') && !acked.has(j.jobId));
  const shown = [...unread, ...running];

  const ack = (jobId) => setAcked((prev) => { const n = new Set(prev); n.add(jobId); saveAcked(n); return n; });
  const ackAll = () => setAcked((prev) => { const n = new Set(prev); unread.forEach((j) => n.add(j.jobId)); saveAcked(n); return n; });

  return (
    <div className="relative" ref={rootRef}>
      <HeaderIconButton
        isDarkMode={isDarkMode}
        onClick={() => setOpen((v) => !v)}
        badge={unread.length}
        title={t('ab.shell.notifTitle')}
        ariaLabel={t('ab.shell.notifTitle')}
      >
        <Bell className="w-4 h-4" />
      </HeaderIconButton>

      {open && (
        <div
          role="dialog"
          aria-label={t('ab.shell.notifTitle')}
          className={`absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto rounded-2xl p-2 z-30 ${isDarkMode
            ? 'bg-gray-900 border border-white/10 shadow-[0_16px_48px_rgba(0,0,0,0.5)]'
            : 'bg-white border border-slate-200 shadow-xl'}`}
        >
          <div className="flex items-center justify-between px-2 py-1.5">
            <span className={`text-xs font-semibold uppercase tracking-wide ${isDarkMode ? 'text-white/50' : 'text-slate-400'}`}>
              {t('ab.shell.notifTitle')}
            </span>
            {unread.length > 0 && (
              <button type="button" onClick={ackAll} className={`text-xs font-medium ${isDarkMode ? 'text-indigo-300 hover:text-indigo-200' : 'text-indigo-600 hover:text-indigo-700'}`}>
                {t('ab.shell.notifAckAll')}
              </button>
            )}
          </div>

          {shown.length === 0 ? (
            <p className={`text-sm px-2 py-4 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{t('ab.shell.notifEmpty')}</p>
          ) : (
            <ul className="space-y-1">
              {shown.map((j) => (
                <li key={j.jobId} className={`flex items-start gap-2 rounded-xl px-2 py-2 text-sm ${isDarkMode ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}>
                  <span className="mt-0.5 flex-shrink-0">
                    {j.status === 'running' && <Loader2 className={`w-4 h-4 animate-spin ${isDarkMode ? 'text-indigo-300' : 'text-indigo-600'}`} />}
                    {j.status === 'done' && <CheckCircle2 className={`w-4 h-4 ${isDarkMode ? 'text-emerald-300' : 'text-emerald-600'}`} />}
                    {j.status === 'error' && <AlertTriangle className={`w-4 h-4 ${isDarkMode ? 'text-red-300' : 'text-red-600'}`} />}
                  </span>
                  <span className={`flex-1 min-w-0 ${isDarkMode ? 'text-white/80' : 'text-slate-700'}`}>
                    {j.status === 'running' && t('ab.shell.notifRunning', { id: j.documentId })}
                    {j.status === 'done' && (
                      <>
                        {t('ab.shell.notifDone', { id: j.documentId })}
                        <button
                          type="button"
                          onClick={() => { setOpen(false); onOpenDocument?.(j.documentId); }}
                          className={`block mt-0.5 text-xs font-medium ${isDarkMode ? 'text-indigo-300 hover:text-indigo-200' : 'text-indigo-600 hover:text-indigo-700'}`}
                        >
                          {t('ab.shell.notifDoneOpen')}
                        </button>
                      </>
                    )}
                    {j.status === 'error' && t('ab.shell.notifError', { id: j.documentId })}
                  </span>
                  {j.status !== 'running' && (
                    <button type="button" onClick={() => ack(j.jobId)} title={t('ab.shell.notifAck')} aria-label={t('ab.shell.notifAck')}
                      className={`flex-shrink-0 p-1 rounded-md ${isDarkMode ? 'text-white/40 hover:text-white hover:bg-white/10' : 'text-slate-400 hover:text-slate-700 hover:bg-slate-100'}`}>
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default AbNotificationBell;
